import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import api from '../../../services/api';
import { EMPTY_FILTERS, toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';

// PM Analitik Faz 2 (spec §9) — E17 "Ekibimin Performansı" veri katmanı.
//   GET v1/performance-workspace/team-context → { period, periods[], skipLevelEnabled, managerEmployeeId } (astsıza 403
//       performance.analytics.forbidden → sayfa kendi durumunu gösterir, tost yok)
//   GET v1/performance-workspace/team?periodId=&team=&title[in]=&stage[eq]=&taskStatus[in]=&due=&pageNumber=&pageSize=
//       → PaginatedList<{ participantId, employeeId, fullName, titleName, isDirect, employeeTasks{open,overdue,done,notOpened},
//          managerTasks{…}, staleGoals, lastOneOnOneAt, resultPublished, resultSeenAt }>
//   GET v1/performance-workspace/agenda?periodId=&team= → { upcomingMeetings[], pendingGoalApprovals, staleGoals{ metric, people[] } }
//   KPI'lar metrik ucundan, ekip süzgeciyle (spec §9.2): orgView=current, managerEmployeeId[eq]=kendisi, includeDescendants.
//   Bugün Yapılacaklar = Gelen Kutum'un kendisi (GET v1/performance/inbox).
// api.js GET'te reject ETMEZ — hata sinyali res.data.status.
export const TEAM_PATH = '/performance/team';
export const FORBIDDEN_KEY = 'performance.analytics.forbidden';
export const TEAM_TABS = ['summary', 'team', 'me'];
export const TEAMS = ['direct', 'subtree'];
export const DUES = ['all', 'overdue', 'week'];
export const KPI_CODES = ['pm.tasksOpen', 'M04', 'M49'];
export const DISTRIBUTION_CODES = ['M07', 'M08'];
export const RESULT_ACK_STAGE = 'result_ack';
export const TODAY_LIMIT = 5;
export const PAGE_SIZE = 20;
export const INBOX_PATH = '/performance/inbox';
export const TEAM_GOALS_PATH = '/performance/team-goals';
export const FILL_FEEDBACK_PATH = '/performance/fill-feedback';

// Sunucu sözlükleriyle aynı (PeriodTaskCalculator.StageOrder, PmMetricCatalog.TaskStatuses) — süzgeç seçenekleri.
export const STAGE_ORDER = [
  'goal_setting', 'goal_evaluation', 'self_review', 'manager_review', 'nomination', 'feedback_360', 'calibration', 'final_result', 'result_ack',
];
export const TASK_STATUSES = ['not_opened', 'open', 'draft', 'submitted', 'returned', 'done', 'exempt', 'cancelled'];

// ---------------------------------------------------------------- URL durumu
const Q = {
  period: 'period', team: 'team', due: 'due', titles: 'title', stage: 'stage', statuses: 'tstatus', tab: 'tab', myPeriod: 'mperiod',
};

const list = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);

export const DEFAULT_TEAM_FILTERS = Object.freeze({
  periodId: null,
  team: 'direct',
  due: 'all',
  titleIds: [],
  stage: null,
  taskStatuses: [],
  tab: 'summary',
  myPeriodId: null,
});

export function parseTeamFilters(search) {
  const p = new URLSearchParams(search || '');
  const team = p.get(Q.team);
  const due = p.get(Q.due);
  const tab = p.get(Q.tab);
  const stage = p.get(Q.stage);
  return {
    periodId: p.get(Q.period) || null,
    team: TEAMS.includes(team) ? team : 'direct',
    due: DUES.includes(due) ? due : 'all',
    titleIds: list(p.get(Q.titles)),
    stage: STAGE_ORDER.includes(stage) ? stage : null,
    taskStatuses: list(p.get(Q.statuses)).filter((s) => TASK_STATUSES.includes(s)),
    tab: TEAM_TABS.includes(tab) ? tab : 'summary',
    myPeriodId: p.get(Q.myPeriod) || null,
  };
}

// Varsayılan değerler URL'ye yazılmaz.
export function serializeTeamFilters(filters) {
  const f = { ...DEFAULT_TEAM_FILTERS, ...filters };
  const p = new URLSearchParams();
  if (f.periodId) p.set(Q.period, f.periodId);
  if (f.team !== 'direct') p.set(Q.team, f.team);
  if (f.due !== 'all') p.set(Q.due, f.due);
  if (f.titleIds.length) p.set(Q.titles, f.titleIds.join(','));
  if (f.stage) p.set(Q.stage, f.stage);
  if (f.taskStatuses.length) p.set(Q.statuses, f.taskStatuses.join(','));
  if (f.tab !== 'summary') p.set(Q.tab, f.tab);
  if (f.myPeriodId) p.set(Q.myPeriod, f.myPeriodId);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function useTeamFilters() {
  const location = useLocation();
  const history = useHistory();
  const filters = useMemo(() => parseTeamFilters(location.search), [location.search]);
  const setFilters = useCallback((next) => {
    const value = typeof next === 'function' ? next(parseTeamFilters(location.search)) : next;
    history.replace({ pathname: location.pathname, search: serializeTeamFilters(value) });
  }, [history, location.pathname, location.search]);
  return { filters, setFilters };
}

// Gelişmiş süzgeçlerden etkin olanlar (silinebilir etiketler).
export function activeAdvancedKeys(filters) {
  const keys = [];
  if (filters.titleIds.length) keys.push('titleIds');
  if (filters.stage) keys.push('stage');
  if (filters.taskStatuses.length) keys.push('taskStatuses');
  return keys;
}

// ---------------------------------------------------------------- uç parametreleri
// Analitik süzgeç biçimi (pmAnalyticsFilters): ekip = güncel hiyerarşide çağıranın altı; doğrudan ekip includeDescendants=false.
export function teamAnalyticsFilters(filters, managerEmployeeId) {
  return {
    ...EMPTY_FILTERS,
    periodId: filters.periodId,
    managerEmployeeId,
    includeDescendants: filters.team === 'subtree',
    orgView: 'current',
  };
}

export function teamListParams(filters, pageNumber, pageSize) {
  const params = { periodId: filters.periodId, team: filters.team, pageNumber, pageSize };
  if (filters.due !== 'all') params.due = filters.due;
  if (filters.titleIds.length) params['title[in]'] = filters.titleIds.join(',');
  if (filters.stage) params['stage[eq]'] = filters.stage;
  if (filters.taskStatuses.length) params['taskStatus[in]'] = filters.taskStatuses.join(',');
  return params;
}

/**
 * Toplu hatırlatma seçimi (spec §5.3, §17/5) — ölçüt, görev ucunun süzgeç sözleşmesidir; kapsam sunucuda (yönetici yalnız kişi
 * kümesindeki çalışan/yönetici görevlerini hatırlatabilir; değerlendirici, İK havuzu ve kendisi hariç). Ekip listesinin süzgeçleri
 * aynen taşınır; "Yalnız Gecikmiş" ya da son tarih "Gecikmiş" → overdue, "Bu Hafta" → dueWithinDays=7. Satırdan açılırsa
 * `assigneeEmployeeId` o kişinin görevleriyle sınırlar.
 */
export function reminderCriteria(filters, managerEmployeeId, { onlyOverdue = false, assigneeEmployeeId = null } = {}) {
  const c = {
    'managerEmployeeId[eq]': managerEmployeeId,
    includeDescendants: String(filters.team === 'subtree'),
    orgView: 'current',
    mandatory: 'true',
  };
  if (filters.titleIds.length) c['titleId[in]'] = filters.titleIds.join(',');
  if (filters.stage) c['stage[eq]'] = filters.stage;
  if (filters.taskStatuses.length) c['status[in]'] = filters.taskStatuses.join(',');
  if (onlyOverdue || filters.due === 'overdue') c.overdue = 'true';
  else if (filters.due === 'week') c.dueWithinDays = '7';
  if (assigneeEmployeeId) c['assigneeEmployeeId[eq]'] = assigneeEmployeeId;
  return c;
}

// Görev sayısı hücresi: açık varsa "N açık (M gecikmiş)", hepsi tamamsa "Tamam", görev yoksa "—".
export function taskCountsText(t, counts) {
  if (!counts) return '—';
  if (counts.open > 0) return t('performance.workspace.team.col.openCount', { open: counts.open, overdue: counts.overdue });
  if (counts.done > 0 && counts.notOpened === 0) return t('performance.workspace.team.col.allDone');
  if (counts.notOpened > 0) return t('performance.workspace.team.col.notOpened');
  return '—';
}

// Bugün Yapılacaklar (spec §9.2): gecikmiş önce, sonra son tarih artan (tarihsiz sonda), sonra tür.
export function sortTodo(items) {
  return [...(items || [])].sort((a, b) => {
    if (!!a.overdue !== !!b.overdue) return a.overdue ? -1 : 1;
    const ad = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY;
    const bd = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY;
    if (ad !== bd) return ad - bd;
    return String(a.kind || '').localeCompare(String(b.kind || ''));
  });
}

// Aşama kapsaması kartının varsayılan aşaması: katalog sırasında %100 olmayan ilk aşama (yoksa ilk aşama).
export function defaultStage(cells) {
  const usable = (cells || []).filter((c) => c.key && c.key !== RESULT_ACK_STAGE);
  const open = usable.find((c) => {
    const m = (c.metrics || [])[0] || c.metric;
    return m && m.value != null && Number(m.value) < 100;
  });
  return (open || usable[0] || {}).key || null;
}

// ---------------------------------------------------------------- uçlar
export class TeamError extends Error {}

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function get(url, params, config = {}) {
  const res = await api.get(url, { params, ...config });
  const data = payload(res);
  if (data === undefined) throw new TeamError(res?.data?.message || 'error');
  return data;
}

// Astsız kullanıcıya 403 sayfa durumudur (tost değil); diğer hatalar yakalayıcının tostuyla gelir ve "yüklenemedi" olur.
export async function fetchTeamContext() {
  const res = await api.get('/v1/performance-workspace/team-context', { skipErrorToast: [FORBIDDEN_KEY], passErrorBody: true });
  if (res?.data?.status) return { context: res.data.data, forbidden: false };
  if (res?.data?.message === FORBIDDEN_KEY) return { context: null, forbidden: true };
  throw new TeamError(res?.data?.message || 'error');
}

export const fetchTeamRows = (filters, pageNumber, pageSize) => get('/v1/performance-workspace/team', teamListParams(filters, pageNumber, pageSize));

export const fetchAgenda = (filters) => get('/v1/performance-workspace/agenda', { periodId: filters.periodId, team: filters.team });

export const fetchTeamMetrics = (analyticsFilters, codes, extra = {}) => get('/v1/performance-analytics/metrics', {
  ...toAnalyticsParams(analyticsFilters), codes: codes.join(','), ...extra,
});

export const fetchStageCoverage = (analyticsFilters) => get('/v1/performance-analytics/breakdown', {
  ...toAnalyticsParams(analyticsFilters), codes: 'M03', dimension: 'stage',
});

export const fetchMyInbox = () => get('/v1/performance/inbox', {});

// 1:1 oluşturma — mevcut uç (yalnız doğrudan yönetici; kapsam sunucuda). Hata tostu yakalayıcıda.
export async function createMeeting(employeeId, scheduledAt) {
  try {
    const res = await api.post('/v1/one-on-ones', { employeeId, scheduledAt });
    return !!res?.data?.status;
  } catch {
    return false;
  }
}
